<template>
    <div class="accordion-item">
        <div
            class="accordion-trigger"
            @click="toggleAccordion(id)"
            :id="activeId === id ? 'accordion-open' : 'accordion-closed'"
        >
            <p v-html="title"></p>
            <span :class="{ 'arrow-up': activeId === id }">↓</span>
        </div>
        <div v-show="activeId === id" class="content">
            <Content v-if="text || internalTitle" class="content-text">
                <h2 class="accordion-title" v-html="internalTitle"></h2>
                <p v-html="text"></p>
            </Content>
            <div class="lottie-limiter">
                <Lottie
                    v-if="
                        animations.mobile_animation &&
                            animations.desktop_animation
                    "
                    :animation="animations"
                />
            </div>
            <div v-if="case_study">
                <router-link
                    :to="case_study.link"
                    @click.native="setAccordionState"
                    class="post-item"
                >
                    <h2
                        class="post-title title-mobile"
                        v-if="case_study.title"
                        v-html="case_study.title"
                    ></h2>
                    <figure class="image" v-if="case_study.image">
                        <img :src="case_study.image" />
                    </figure>
                    <div class="info accordion-info">
                        <h2
                            class="post-title title-desktop"
                            v-if="case_study.title"
                            v-html="case_study.title"
                        ></h2>
                        <p
                            class="excerpt"
                            v-if="case_study.text"
                            v-html="case_study.text"
                        ></p>
                    </div>
                </router-link>
            </div>
        </div>
    </div>
</template>

<script>
import Lottie from "./Lottie.vue";
import Content from "@/components/ui/Content.vue";

export default {
    name: "accordion-item",
    props: {
        item: {
            type: Object
        },
        id: {
            type: Number
        },
        activeId: {
            type: Number
        },
        toggleAccordion: {
            type: Function
        }
    },

    watch: {
        activeId: function(newId, oldId) {
            if(oldId !== null && newId !== oldId && newId === this.id && newId !== 0) {
                this.$el.scrollIntoView(true)
            }
        }
    },

    data() {
        return {
            isOpen: false,
            title: "",
            internalTitle: "",
            animations: { mobile_animation: "", desktop_animation: "" },
            case_study: { image: "", text: "", title: "", link: "" },
            text: ""
        };
    },

    mounted() {
        this.title = this.item.accordion_title;
        this.internalTitle = this.item.internal_title;
        this.animations = this.item.animations;
        this.case_study = {
            ...this.item.case_study,
            link: this.item.case_study?.link.replace(
                window.globals.home_url,
                this.$base_url
            )
        };
        this.isOpen = this.activeId === this.id || this.$store.state.open_accordion_id === this.id;

        if(this.isOpen) {
            // there's a "race condition" on production where the scrolling happens before the opening of the accordion,
            // causing the user to land on a much lower point in the page than it should
            // - also window.scrollY returns 0 after first route change
            setTimeout(() => {
                this.$el.scrollIntoView({block: "end"})
            }, 75)
        }

        this.text = this.item.text;
    },

    components: {
        Lottie,
        Content
    },

    methods: {
        getWindowScrollY() {
            return window.scrollY;
        },
        setAccordionState(e) {
            this.$store.state.accordion_counter = 0;
            this.$store.state.open_accordion_id = this.id;
            this.$store.state.accordion_open = true;
        }
    }
};
</script>

<style lang="scss" scoped>
.accordion-item {
    border-bottom: 2px solid var(--font-color);
    font-size: var(--large-size);

    &:first-child {
        border-top: 2px solid var(--font-color);
    }

    .accordion-trigger {
        display: flex;
        align-items: center;
        justify-content: space-between;
        cursor: pointer;
        padding: var(--padding-50) 0;

        p {
            margin: 0;
        }
    }

    .arrow-up {
        transform: rotate(180deg);
    }

    .lottie-limiter {
        max-width: 80%;
        width: 100%;
        margin-inline: auto;
    }

    .accordion-item .content {
        // margin: var(--margin) 0;
        display: flex;
        flex-direction: column;
        gap: 1em;
    }

    .content-text {
        font-size: inherit;
    }

    .post-item {
        margin: var(--padding) 0;
        box-sizing: border-box;
        display: flex;
        flex-direction: row;

        &:hover {
            h2 {
                text-decoration: underline;
            }

            img {
                transform: scale(0.95);
            }
        }

        .image {
            width: 46%;
            display: block;
            position: relative;

            &::before {
                content: "";
                display: block;
                padding-bottom: 70%;
            }

            img {
                width: 100%;
                height: 100%;
                position: absolute;
                left: 0;
                top: 0;
                display: block;
                object-fit: cover;
                transition: all 0.2s ease-out;
            }
        }

        .info {
            width: 50%;
            box-sizing: border-box;
            padding-left: var(--padding);
            font-size: var(--base-size);
        }

        .post-title {
            font-size: var(--large-size);
            margin: 0 0 2rem;
        }

        .post-title.title-mobile {
            display: none;
        }

        .post-title.title-desktop {
            display: block;
        }
    }
}
@media only screen and (max-width: 767px) {
    .accordion-item {
        .content-text {
            font-size: var(--base-size);
        }
        .arrow {
            height: 28px;
        }
        .accordion-title {
            font-size: var(--large-size) !important;
        }
        .post-item {
            margin: var(--padding) 0;
            display: flex;
            flex-direction: column;

            .image {
                width: 80%;
                margin: 0 auto 1em;

                &::before {
                    padding-bottom: 75%;
                }
            }

            .info {
                width: 100%;
                padding: 0;
                margin: 0;
                text-align: left;

                .excerpt {
                    margin-top: 0;
                }
            }

            .post-title {
                font-size: var(--large-size);
            }

            .post-title {
                margin-bottom: 0.5em;
                text-decoration: none;
            }

            .post-title.title-desktop {
                display: none;
            }

            .post-title.title-mobile {
                display: block;
                margin-bottom: 1em;
            }

            .excerpt {
                font-size: var(--base-size);
                text-decoration: none;
            }
        }
    }
}
</style>
